
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Sovereignty() {
  const { t } = useTranslation();

  return (
    <section
      id="sovereignty"
      className="relative py-24 bg-white overflow-hidden"
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#0D9488]/30 to-transparent"></div>
      <div className="absolute top-20 right-10 w-96 h-96 bg-[#0D9488]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#10B981]/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D9488]/10 rounded-full mb-6">
            <div className="w-2 h-2 bg-[#0D9488] rounded-full animate-pulse"></div>
            <span className="text-[#0D9488] text-sm font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('sovereignty.badge')}
            </span>
          </div>

          <h2
            className="text-4xl md:text-5xl font-bold text-[#1E293B] mb-6 leading-tight"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t('sovereignty.title')}
          </h2>

          <p
            className="text-lg text-[#64748B] max-w-4xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t('sovereignty.subtitle')}
          </p>
        </div>

        {/* Comparison Section - ABRS vs Competitors */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* Left - Competitors (Replacement) */}
          <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl overflow-hidden border-2 border-gray-200">
            {/* Image Header */}
            <div className="relative h-48 w-full overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/9c574cd310805ba1c3ae5784326190be.jpg"
                alt="Remplacement coûteux de bagages"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-gray-50"></div>
            </div>

            <div className="p-8">
              <div className="absolute top-6 right-6">
                <div className="px-4 py-2 bg-red-100 text-red-600 rounded-full text-xs font-bold">
                  {t('sovereignty.competitorBadge')}
                </div>
              </div>

              <div className="mb-6">
                <div className="w-16 h-16 bg-gray-300 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-shopping-bag-line text-gray-600 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('sovereignty.competitorTitle')}
                </h3>
                <p className="text-gray-500 text-sm">{t('sovereignty.competitorSubtitle')}</p>
              </div>

              <div className="space-y-4">
                {[
                  { icon: 'ri-money-euro-circle-line', text: t('sovereignty.competitorPoint1'), color: 'text-red-500' },
                  { icon: 'ri-time-line', text: t('sovereignty.competitorPoint2'), color: 'text-red-500' },
                  { icon: 'ri-global-line', text: t('sovereignty.competitorPoint3'), color: 'text-red-500' },
                  { icon: 'ri-leaf-line', text: t('sovereignty.competitorPoint4'), color: 'text-red-500' },
                  { icon: 'ri-emotion-unhappy-line', text: t('sovereignty.competitorPoint5'), color: 'text-red-500' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm font-medium">{t('sovereignty.avgCostLabel')}</span>
                  <span className="text-2xl font-bold text-red-600">€250-500</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - ABRS (Repair) */}
          <div className="relative bg-gradient-to-br from-[#0D9488]/5 to-[#10B981]/5 rounded-3xl overflow-hidden border-2 border-[#0D9488]">
            {/* Image Header */}
            <div className="relative h-48 w-full overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20luggage%20repair%20workshop%20with%20skilled%20technician%20fixing%20suitcase%20using%20precision%20tools%20clean%20modern%20workspace%20showing%20quality%20craftsmanship%20and%20sustainable%20repair%20service%20with%20repaired%20luggage%20looking%20brand%20new%20bright%20professional%20lighting&width=800&height=400&seq=sovereignty-abrs-repair&orientation=landscape"
                alt="Réparation professionnelle ABRS"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-[#0D9488]/5"></div>
            </div>

            <div className="p-8">
              <div className="absolute top-6 right-6">
                <div className="px-4 py-2 bg-[#0D9488] text-white rounded-full text-xs font-bold">
                  {t('sovereignty.abrsBadge')}
                </div>
              </div>

              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0D9488] to-[#10B981] rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1E293B] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('sovereignty.abrsTitle')}
                </h3>
                <p className="text-[#64748B] text-sm">{t('sovereignty.abrsSubtitle')}</p>
              </div>

              <div className="space-y-4">
                {[
                  { icon: 'ri-money-euro-circle-line', text: t('sovereignty.abrsPoint1'), color: 'text-[#0D9488]' },
                  { icon: 'ri-timer-flash-line', text: t('sovereignty.abrsPoint2'), color: 'text-[#0D9488]' },
                  { icon: 'ri-map-pin-line', text: t('sovereignty.abrsPoint3'), color: 'text-[#0D9488]' },
                  { icon: 'ri-leaf-line', text: t('sovereignty.abrsPoint4'), color: 'text-[#0D9488]' },
                  { icon: 'ri-emotion-happy-line', text: t('sovereignty.abrsPoint5'), color: 'text-[#0D9488]' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-lg bg-[#0D9488]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} ${item.color} text-sm`}></i>
                    </div>
                    <p className="text-[#1E293B] text-sm leading-relaxed font-medium">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-[#0D9488]/30">
                <div className="flex items-center justify-between">
                  <span className="text-[#64748B] text-sm font-medium">{t('sovereignty.avgCostLabel')}</span>
                  <span className="text-2xl font-bold text-[#0D9488]">€30-60</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ROI Calculator Section */}
        <div className="bg-gradient-to-br from-[#1E293B] to-[#334155] rounded-3xl p-10 mb-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('sovereignty.roiTitle')}
            </h3>
            <p className="text-white/70 text-sm max-w-2xl mx-auto">
              {t('sovereignty.roiSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-[#0D9488] rounded-xl flex items-center justify-center mb-4">
                <i className="ri-percent-line text-white text-2xl"></i>
              </div>
              <div className="text-4xl font-bold text-white mb-2">-88%</div>
              <p className="text-white/70 text-sm">{t('sovereignty.roiMetric1')}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-[#10B981] rounded-xl flex items-center justify-center mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <div className="text-4xl font-bold text-white mb-2">1-2h</div>
              <p className="text-white/70 text-sm">{t('sovereignty.roiMetric2')}</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-12 h-12 bg-[#F59E0B] rounded-xl flex items-center justify-center mb-4">
                <i className="ri-emotion-happy-line text-white text-2xl"></i>
              </div>
              <div className="text-4xl font-bold text-white mb-2">+53%</div>
              <p className="text-white/70 text-sm">{t('sovereignty.roiMetric3')}</p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#0D9488] rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-calculator-line text-white text-xl"></i>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('sovereignty.exampleTitle')}
                </h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  {t('sovereignty.exampleText')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center items-center gap-6 mb-12">
          <div className="flex items-center gap-3 bg-[#0D9488]/10 px-5 py-3 rounded-xl border border-[#0D9488]/20">
            <i className="ri-shield-check-line text-[#0D9488] text-xl"></i>
            <span className="text-[#1E293B] font-medium text-sm">{t('sovereignty.certified')}</span>
          </div>
          <div className="flex items-center gap-3 bg-[#10B981]/10 px-5 py-3 rounded-xl border border-[#10B981]/20">
            <i className="ri-plane-line text-[#10B981] text-xl"></i>
            <span className="text-[#1E293B] font-medium text-sm">{t('sovereignty.hub')}</span>
          </div>
          <div className="flex items-center gap-3 bg-[#F59E0B]/10 px-5 py-3 rounded-xl border border-[#F59E0B]/20">
            <i className="ri-time-line text-[#F59E0B] text-xl"></i>
            <span className="text-[#1E293B] font-medium text-sm">{t('sovereignty.available')}</span>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#0D9488] to-[#0F766E] text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-[#0D9488]/25 transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <i className="ri-file-text-line text-xl"></i>
            {t('sovereignty.cta')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
